import type { ApiClient } from "../../lib/http/client";
import { ApiError, toApiError } from "../../lib/http/errors";
import type { ErrorCode } from "../../lib/http/errors";
import type { SessionGrant } from "../../types/session";
import type { LaunchFragment } from "./fragment";
import type { SessionStore } from "./storage";

/**
 * 세션 상태 기계 (SR-AUTH-01/02 · FS-01-01 · FS-C1-02).
 *
 *   idle ─start─▶ pending(ticket|sso|restore) ─▶ ready ─refresh─▶ ready …
 *                    │                              └─401─▶ error(FE-304)
 *                    └─실패─▶ error(code)       none(티켓·보관 세션 없음)
 *
 * React 밖에 두어 StrictMode 이중 실행과 무관하게 한 번만 교환한다(일회용 티켓).
 */
export type SessionVia = "ticket" | "sso" | "restore";

export type SessionState =
  | { status: "idle" }
  | { status: "pending"; via: SessionVia }
  | { status: "none" }
  | { status: "ready"; session: SessionGrant }
  | { status: "error"; code: ErrorCode; retryable: boolean };

export interface SessionManagerOptions {
  api: ApiClient;
  store: SessionStore;
  now?: () => number;
  /** 만료 몇 ms 전에 갱신할지(상한). 기본 5분 */
  refreshLeadMs?: number;
  /** 갱신이 네트워크로 실패했을 때 다시 시도할 간격. 기본 30초 */
  retryDelayMs?: number;
}

export const DEFAULT_REFRESH_LEAD_MS = 5 * 60_000;
export const DEFAULT_RETRY_DELAY_MS = 30_000;

type Listener = (s: SessionState) => void;

export class SessionManager {
  private state: SessionState = { status: "idle" };
  private readonly listeners = new Set<Listener>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private refreshAt = Number.POSITIVE_INFINITY;
  private inflight: Promise<void> | null = null;
  private lastAttempt: (() => Promise<void>) | null = null;
  private readonly api: ApiClient;
  private readonly store: SessionStore;
  private readonly now: () => number;
  private readonly lead: number;
  private readonly retryDelay: number;

  constructor(o: SessionManagerOptions) {
    this.api = o.api;
    this.store = o.store;
    this.now = o.now ?? (() => Date.now());
    this.lead = o.refreshLeadMs ?? DEFAULT_REFRESH_LEAD_MS;
    this.retryDelay = o.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;
  }

  getState = (): SessionState => this.state;

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  /**
   * 인증이 필요한 제어 평면 호출(S2~) — 현재 Bearer 토큰을 붙인다. 세션이 없으면 FE-304.
   * 401 이면 세션을 한 번 갱신하고 다시 부른다(갱신도 실패하면 세션 상태가 FE-304 로 바뀐다).
   */
  request = async <T>(
    path: string,
    init: RequestInit & { as?: "json" | "bytes" } = {},
  ): Promise<T> => {
    const token = this.token;
    if (!token) throw new ApiError("FE-304", 401, "세션이 없습니다");
    try {
      return await this.api.request<T>(path, { ...init, token });
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 401) throw e;
      await this.refreshNow();
      const again = this.token;
      if (!again || again === token) throw e;
      return this.api.request<T>(path, { ...init, token: again });
    }
  };

  /**
   * 세션 없이도 부를 수 있는 호출(P3-S5 GET /api/share/{token} — 세션이 있으면 Bearer 를 붙이고, 없으면 그냥 부른다).
   * 401 갱신 재시도는 하지 않는다(관찰자 화면은 세션 없음 자체가 한 상태다).
   */
  optionalRequest = <T>(
    path: string,
    init: RequestInit & { as?: "json" | "bytes" } = {},
  ): Promise<T> => {
    const token = this.token;
    return token ? this.request<T>(path, init) : this.api.request<T>(path, init);
  };

  /** 현재 Bearer 토큰(없으면 null) — API 호출부가 쓴다. */
  get token(): string | null {
    return this.state.status === "ready" ? this.state.session.token : null;
  }

  /**
   * 부팅 시 한 번. 우선순위: #error > #ticket > #bffToken > 보관 세션 > none.
   * 이미 시작했으면 아무것도 하지 않는다.
   */
  start(frag: LaunchFragment): Promise<void> {
    if (this.state.status !== "idle") return this.inflight ?? Promise.resolve();
    if (frag.error) {
      this.store.clear();
      this.set({ status: "error", code: frag.error, retryable: false });
      return Promise.resolve();
    }
    if (frag.ticket) {
      const ticket = frag.ticket;
      // 새 런치는 이전 탭 세션을 대체한다(다른 사용자·코스일 수 있다).
      this.store.clear();
      return this.attempt("ticket", () => this.api.exchange(ticket));
    }
    if (frag.bffToken) return this.signInWithBffToken(frag.bffToken);
    const stored = this.store.load();
    if (stored) return this.restore(stored);
    this.set({ status: "none" });
    return Promise.resolve();
  }

  /** 문 B — Campus BFF 토큰으로 세션을 연다. */
  signInWithBffToken(bffToken: string): Promise<void> {
    if (this.state.status === "pending") return this.inflight ?? Promise.resolve();
    this.store.clear();
    return this.attempt("sso", () => this.api.campusSso(bffToken));
  }

  /** 네트워크 실패 뒤 같은 시도를 다시 한다(FS-C1 '다시 해 보기'). */
  retry(): Promise<void> {
    if (this.state.status !== "error" || !this.state.retryable || !this.lastAttempt)
      return Promise.resolve();
    return this.lastAttempt();
  }

  /** 탭이 다시 보일 때 — 백그라운드 탭은 타이머가 늦어지므로 갱신 시각이 지났으면 바로 갱신한다. */
  wake(): Promise<void> {
    if (this.state.status === "ready" && this.now() >= this.refreshAt) return this.refreshNow();
    return Promise.resolve();
  }

  async refreshNow(): Promise<void> {
    if (this.state.status !== "ready") return;
    if (this.inflight) return this.inflight;
    const current = this.state.session;
    this.clearTimer();
    const run = (async () => {
      try {
        this.accept(await this.api.refresh(current.token));
      } catch (e) {
        const err = toApiError(e);
        if (err.retryable && this.now() < Date.parse(current.expiresAt)) {
          // 아직 토큰이 살아 있으면 조용히 다시 시도한다.
          this.schedule(this.now() + this.retryDelay);
          return;
        }
        this.fail(err.retryable ? "FE-304" : err.code, false);
      }
    })();
    this.inflight = run;
    try {
      await run;
    } finally {
      this.inflight = null;
    }
  }

  signOut(): void {
    this.clearTimer();
    this.store.clear();
    this.set({ status: "none" });
  }

  dispose(): void {
    this.clearTimer();
    this.listeners.clear();
  }

  // ── 내부 ────────────────────────────────────────────────────────────

  private attempt(via: SessionVia, call: () => Promise<SessionGrant>): Promise<void> {
    const run = async () => {
      this.set({ status: "pending", via });
      try {
        this.accept(await call());
      } catch (e) {
        const err = toApiError(e);
        this.fail(err.code, err.retryable);
      }
    };
    this.lastAttempt = () => this.track(run());
    return this.lastAttempt();
  }

  private restore(stored: SessionGrant): Promise<void> {
    if (Date.parse(stored.expiresAt) <= this.now()) {
      this.fail("FE-304", false);
      return Promise.resolve();
    }
    const run = async () => {
      this.set({ status: "pending", via: "restore" });
      try {
        const claims = await this.api.me(stored.token);
        this.accept({ ...stored, ...claims, token: stored.token });
      } catch (e) {
        const err = toApiError(e);
        // 오프라인이면 보관 세션으로 계속한다 — 갱신 타이머가 뒤에 다시 확인한다.
        if (err.retryable) this.accept(stored);
        else this.fail(err.code, false);
      }
    };
    this.lastAttempt = () => this.track(run());
    return this.lastAttempt();
  }

  private track(p: Promise<void>): Promise<void> {
    this.inflight = p;
    return p.finally(() => {
      if (this.inflight === p) this.inflight = null;
    });
  }

  private accept(s: SessionGrant): void {
    this.store.save(s);
    this.set({ status: "ready", session: s });
    const exp = Date.parse(s.expiresAt);
    const remaining = exp - this.now();
    // 남은 시간의 절반과 lead 중 작은 값만큼 앞당겨 갱신한다(짧은 토큰에서도 만료 전에 돈다).
    this.schedule(exp - Math.min(this.lead, Math.max(0, remaining) / 2));
  }

  private fail(code: ErrorCode, retryable: boolean): void {
    this.clearTimer();
    if (!retryable) this.store.clear();
    this.set({ status: "error", code, retryable });
  }

  private schedule(at: number): void {
    this.clearTimer();
    this.refreshAt = at;
    const delay = Math.max(0, at - this.now());
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.refreshNow();
    }, delay);
  }

  private clearTimer(): void {
    if (this.timer != null) clearTimeout(this.timer);
    this.timer = null;
    this.refreshAt = Number.POSITIVE_INFINITY;
  }

  private set(s: SessionState): void {
    this.state = s;
    for (const l of this.listeners) l(s);
  }
}
